import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Clock, Sparkles, MessageSquare } from "lucide-react";
import { Screen, TopBar, Logo, ScrollArea, BottomBar, PrimaryButton } from "@/components/jk/ui";
import { Card, Pill, SectionLabel, FullScreenLoader } from "@/components/jk/kit";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatShortMoney, formatDate, formatTime } from "@/lib/format";
import { loadRazorpay } from "@/lib/razorpay";
import api from "@/lib/api";

const useCountdown = (until) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!until) return;
    const target = new Date(until).getTime();
    const tick = () => setLeft(Math.max(0, Math.floor((target - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [until]);
  const m = String(Math.floor(left / 60)).padStart(2, "0");
  const s = String(left % 60).padStart(2, "0");
  return { left, label: `${m}:${s}` };
};

const PERKS = ["Unlimited client bookings", "Automated reminders & confirmations", "Client history & notes", "Your public booking page"];

const Paywall = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/astrologer/paywall").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const pending = data?.pending_booking;
  const { left, label } = useCountdown(pending?.slot_held_until);

  if (loading) return <FullScreenLoader />;
  const stats = data?.stats || { total_bookings: 0, unique_clients: 0, revenue: 0 };

  const goActive = async () => { await refresh(); navigate("/dashboard", { replace: true }); };

  const subscribe = async () => {
    setSubscribing(true);
    setError("");
    try {
      const mode = data?.billing_mode || "manual";
      if (mode === "razorpay_autopay") {
        // Phase 2 — Razorpay UPI AutoPay (recurring mandate)
        const { data: order } = await api.post("/astrologer/subscribe-order");
        if (order.mock) { await api.post("/astrologer/subscribe"); return goActive(); }
        const ok = await loadRazorpay();
        if (!ok) { setError("Could not load the payment gateway."); setSubscribing(false); return; }
        const rzp = new window.Razorpay({
          key: order.key_id, subscription_id: order.subscription_id,
          name: "JothidaKural Pro", description: `${formatINR(data?.price || 750)} / month`,
          prefill: { email: order.email, name: order.name }, theme: { color: "#111111" },
          handler: async (resp) => {
            try {
              await api.post("/astrologer/verify-subscription", {
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_subscription_id: resp.razorpay_subscription_id,
                razorpay_signature: resp.razorpay_signature,
              });
              goActive();
            } catch { setError("Subscription could not be verified. Please try again."); setSubscribing(false); }
          },
          modal: { ondismiss: () => setSubscribing(false) },
        });
        rzp.on("payment.failed", () => { setError("Payment failed. Please try again."); setSubscribing(false); });
        rzp.open();
        return;
      }

      // Phase 1 — one-time monthly payment (renews your access for 30 days)
      const { data: order } = await api.post("/astrologer/subscription/pay-order");
      if (order.mock) { await api.post("/astrologer/subscription/pay-mock"); return goActive(); }
      const ok = await loadRazorpay();
      if (!ok) { setError("Could not load the payment gateway."); setSubscribing(false); return; }
      const rzp = new window.Razorpay({
        key: order.key_id, order_id: order.order_id, amount: order.amount, currency: order.currency,
        name: "JothidaKural Pro", description: `${formatINR(order.price)} · 1 month`,
        prefill: { email: order.email, name: order.name }, theme: { color: "#111111" },
        handler: async (resp) => {
          try {
            await api.post("/astrologer/subscription/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            goActive();
          } catch { setError("Payment could not be verified. Please try again."); setSubscribing(false); }
        },
        modal: { ondismiss: () => setSubscribing(false) },
      });
      rzp.on("payment.failed", () => { setError("Payment failed. Please try again."); setSubscribing(false); });
      rzp.open();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start subscription.");
      setSubscribing(false);
    }
  };

  return (
    <Screen testid="paywall-screen">
      <TopBar center={<Logo className="h-[16px] w-auto" />} />

      <ScrollArea className="px-6 pt-2">
        <h1 className="text-[28px] font-bold leading-tight tracking-[-0.02em] text-[#111111]">You&apos;ve unlocked all 5 free bookings</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-[#666666]">Subscribe to keep accepting new clients and unlock your pending booking.</p>

        <div className="mt-4 flex gap-1.5" data-testid="free-dots">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-2 flex-1 rounded-full bg-[#111111]" />
          ))}
        </div>

        {/* Pending booking */}
        {pending && (
          <Card className="mt-6" data-testid="pending-booking">
            <div className="flex items-center justify-between">
              <SectionLabel>Pending booking</SectionLabel>
              <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold ${left > 0 ? "bg-[var(--jk-peach)] text-[#111111]" : "bg-[#f2f0ec] text-[#999]"}`} data-testid="slot-countdown">
                <Clock size={13} strokeWidth={2} /> {left > 0 ? `Held ${label}` : "Hold expired"}
              </span>
            </div>
            <h2 className="mt-3 text-[18px] font-bold text-[#111111]">{pending.client_name}</h2>
            <div className="mt-1.5 flex flex-wrap gap-2">
              <Pill tone="periwinkle">{pending.service_name}</Pill>
              <Pill tone="neutral">{formatDate(pending.booking_date)} · {formatTime(pending.booking_time)}</Pill>
            </div>
            <p className="mt-3 text-[14px] font-semibold text-[#111111]">{formatINR(pending.fee_inr)}</p>
          </Card>
        )}

        {/* Stats */}
        <div className="mt-4 grid grid-cols-3 gap-3" data-testid="paywall-stats">
          {[
            { v: stats.total_bookings, l: "Bookings" },
            { v: stats.unique_clients, l: "Clients" },
            { v: formatShortMoney(stats.revenue), l: "Earned" },
          ].map((s) => (
            <div key={s.l} className="rounded-[14px] border border-[#e9e6e1] bg-white p-3.5 text-center">
              <p className="text-[18px] font-bold text-[#111111]">{s.v}</p>
              <p className="mt-1 text-[11px] text-[#666666]">{s.l}</p>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <Card dark className="mt-4">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[var(--jk-butter)]" />
            <SectionLabel className="text-white/60">JothidaKural Pro</SectionLabel>
          </div>
          <div className="mt-3 flex items-end gap-1">
            <span className="text-[34px] font-bold leading-none">{formatINR(data?.price || 750)}</span>
            <span className="mb-1 text-[14px] text-white/60">/month</span>
          </div>
          <div className="mt-4 space-y-2.5">
            {PERKS.map((p) => (
              <div key={p} className="flex items-center gap-2.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white"><Check size={12} strokeWidth={3} className="text-[#111111]" /></span>
                <span className="text-[13px] text-white/90">{p}</span>
              </div>
            ))}
          </div>
        </Card>
        <div className="h-4" />
      </ScrollArea>

      <BottomBar>
        {error && <p className="mb-3 text-center text-[13px] text-[#d23b2f]" data-testid="paywall-error">{error}</p>}
        <PrimaryButton testid="subscribe-button" onClick={subscribe} disabled={subscribing}>
          {subscribing ? "Activating…" : `Subscribe for ${formatINR(data?.price || 750)}/month`}
        </PrimaryButton>
        <button data-testid="talk-to-us" className="mt-3 flex w-full items-center justify-center gap-1.5 text-[13px] font-medium text-[#666666]">
          <MessageSquare size={14} strokeWidth={1.75} /> Talk to us
        </button>
      </BottomBar>
    </Screen>
  );
};

export default Paywall;
